import { forwardRef, useMemo, createRef, useEffect, createElement } from 'react';
import cn from 'classnames';
import { Spinner } from '../Spinner/Spinner.js';
import { useLocalization } from '../Provider/Provider.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { getInputClasses } from '../Input/util.js';
import { getNextIndexFromKey } from '../../utils/hooks/useListKeyboardNavigation.js';
import { useListScrollTo } from '../../utils/hooks/useListScrollTo.js';

const getId = (id, value) => `${id}_${value}`;

const getNextEnabledItem = (event, data, index) => {
  const nextIndex = getNextIndexFromKey(event.key, data.length, index);

  if (nextIndex) {
    if (nextIndex === index) {
      return index;
    } else if (data[nextIndex] && data[nextIndex].disabled) {
      return getNextEnabledItem(event, data, nextIndex);
    }
  }

  return nextIndex;
};

const ScrollableList = /*#__PURE__*/forwardRef(function ScrollableList(props, ref) {
  const {
    data,
    disabled,
    highlighted,
    id,
    invalid: _,
    loading,
    onChange: setCurrentIndex,
    onClick,
    onFocus,
    onKeyDown,
    readOnly,
    scrollOnFocus = false,
    value: currentIndex,
    multiselect,
    selectedIndexes = [],
    allOptionsSelected = false,
    ...otherProps
  } = props;
  const listRef = useProxiedRef(ref);
  const itemRefs = useMemo(() => data.map(() => createRef()), [data]);
  const {
    texts
  } = useLocalization();
  const {
    scrollTo
  } = useListScrollTo(listRef, itemRefs);
  useEffect(() => {
    var _itemRefs$currentInde;

    if (currentIndex && (_itemRefs$currentInde = itemRefs[currentIndex]) !== null && _itemRefs$currentInde !== void 0 && _itemRefs$currentInde.current) {
      var _itemRefs$currentInde2;

      (_itemRefs$currentInde2 = itemRefs[currentIndex].current) === null || _itemRefs$currentInde2 === void 0 ? void 0 : _itemRefs$currentInde2.scrollIntoView({
        block: 'center'
      });
    }
  }, []);
  useEffect(() => {
    scrollTo(currentIndex);
  }, [currentIndex]);

  const handleKeyDown = event => {
    const nextIndex = getNextEnabledItem(event, data, currentIndex);

    if (nextIndex !== undefined && nextIndex !== currentIndex) {
      event.preventDefault();
      scrollTo(nextIndex);
      setCurrentIndex(nextIndex);
    }

    if (onKeyDown) {
      event.persist();
      const index = nextIndex !== undefined ? nextIndex : currentIndex;
      onKeyDown(event, index);
    }
  };

  const handleClick = index => event => {
    setCurrentIndex(index);

    if (onClick) {
      event.persist();
      onClick(event, index);
    }
  };

  const handleFocus = event => {
    if (scrollOnFocus) {
      scrollTo(currentIndex);
    }

    if (onFocus) {
      event.persist();
      onFocus(event);
    }
  };

  const getOptionCheckedState = (optionValue, index) => {
    if (optionValue === '#ALL-OPTIONS#') {
      return allOptionsSelected;
    } else if (!optionValue || !selectedIndexes) {
      return false;
    } else {
      return selectedIndexes.findIndex(i => i === index) !== -1;
    }
  };

  const list = { ...otherProps,
    className: cn('inline-flex flex-col list-none !p-0 m-0 overflow-y-auto h-auto', getInputClasses(props), {
      'yt-list--multiselect': multiselect,
      'pointer-events-none': disabled,
      'cursor-not-allowed': disabled || readOnly
    }, otherProps.className),
    disabled,
    id,
    onFocus: !disabled && !readOnly ? handleFocus : undefined,
    onKeyDown: !disabled && !readOnly ? handleKeyDown : undefined,
    readOnly,
    ref: listRef,
    role: 'listbox',
    tabIndex: otherProps.tabIndex || 0
  };
  const options = data.map((option, index) => {
    const depth = option.path ? option.path.split('.').length - 1 : 0;
    return {
      'aria-selected': multiselect ? getOptionCheckedState(String(option.value), index) : currentIndex === index,
      'data-focused': currentIndex === index,
      children: option.text,
      className: cn('flex items-center px-3 w-full cursor-pointer bg-white flex-[0_0_2rem] focus:wcag-blue focus:border-blue', {
        'sticky top-0 font-bold': depth === 0 && !!option.hasChildren
      }),
      disabled: option.disabled,
      icon: option.icon,
      id: getId(id, option.value),
      key: getId(id, option.value),
      onClick: !disabled && !readOnly ? handleClick(index) : undefined,
      ref: itemRefs[index],
      role: 'option',
      style: depth > 0 ? {
        paddingLeft: `${depth + 1}rem`
      } : undefined
    };
  });
  return createElement("ul", Object.assign({}, list, {
    "data-taco": "scrollable-list"
  }), loading ? createElement("li", {
    className: "yt-list__empty"
  }, createElement("span", null, createElement(Spinner, {
    delay: 0
  })), createElement("span", null, texts.listbox.loading)) : options.length ? options.map(({
    children,
    icon,
    ...optionProps
  }) => createElement("li", Object.assign({}, optionProps), icon, createElement("span", {
    className: "flex-grow truncate text-left"
  }, children), multiselect && createElement(Checkbox, {
    checked: optionProps['aria-selected'],
    onChange: () => null,
    className: "pointer-events-none ml-2 self-center p-px"
  }))) : createElement("li", {
    className: "yt-list__empty"
  }, createElement("span", null, texts.listbox.empty)));
});

export { ScrollableList, getId };
//# sourceMappingURL=ScrollableList.js.map
